"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  Loader2,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { faNum, faToman } from "@/lib/format";
import type { AIProduct } from "./types";

type Props = { onRefresh: () => void };

type FormState = {
  name: string;
  description: string;
  priceToman: string;
  duration: string;
  icon: string;
  active: boolean;
  sortOrder: string;
};

const EMPTY_FORM: FormState = {
  name: "",
  description: "",
  priceToman: "0",
  duration: "",
  icon: "🤖",
  active: true,
  sortOrder: "0",
};

export default function AiProductsManager({ onRefresh }: Props) {
  const [products, setProducts] = React.useState<AIProduct[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<AIProduct | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = React.useState(false);

  const [deleteTarget, setDeleteTarget] = React.useState<AIProduct | null>(
    null,
  );
  const [deleting, setDeleting] = React.useState(false);

  const [togglingId, setTogglingId] = React.useState<string | null>(null);

  const loadProducts = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/ai-products", {
        cache: "no-store",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت محصولات");
      }
      setProducts(data.products ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت محصولات";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  }

  function openEdit(p: AIProduct) {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description ?? "",
      priceToman: String(p.priceToman ?? 0),
      duration: p.duration ?? "",
      icon: p.icon ?? "",
      active: p.active,
      sortOrder: String(p.sortOrder ?? 0),
    });
    setFormOpen(true);
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("نام محصول را وارد کنید.");
      return;
    }
    const price = Number(form.priceToman);
    if (!Number.isFinite(price) || price < 0) {
      toast.error("مبلغ را به عدد وارد کنید.");
      return;
    }
    const sort = Number(form.sortOrder) || 0;

    setSubmitting(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || "",
        priceToman: price,
        duration: form.duration.trim() || null,
        icon: form.icon.trim() || null,
        active: form.active,
        sortOrder: sort,
      };
      let res: Response;
      if (editing) {
        res = await fetch("/api/admin/ai-products", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: editing.id, ...body }),
        });
      } else {
        res = await fetch("/api/admin/ai-products", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      }
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در ذخیره محصول");
      }
      toast.success(
        editing ? "محصول ویرایش شد." : "محصول جدید ساخته شد.",
      );
      setFormOpen(false);
      loadProducts();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در ذخیره محصول";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(p: AIProduct, next: boolean) {
    setTogglingId(p.id);
    try {
      const res = await fetch("/api/admin/ai-products", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: p.id, active: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در تغییر وضعیت محصول");
      toast.success(next ? "محصول فعال شد." : "محصول غیرفعال شد.");
      loadProducts();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در تغییر وضعیت محصول";
      toast.error(msg);
    } finally {
      setTogglingId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `/api/admin/ai-products?id=${encodeURIComponent(deleteTarget.id)}`,
        { method: "DELETE" },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در حذف محصول");
      toast.success("محصول حذف شد.");
      setDeleteTarget(null);
      loadProducts();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در حذف محصول";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="border-b">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="size-5 text-primary" />
            مدیریت محصولات هوش مصنوعی
          </CardTitle>
          <Button className="bg-primary hover:bg-primary/90" onClick={openCreate}>
            <Plus className="size-4" />
            افزودن محصول
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-destructive">
            {error}
          </div>
        ) : products.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            هنوز محصولی تعریف نشده است.
          </div>
        ) : (
          <div className="max-h-[28rem] overflow-y-auto scrollbar-thin">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead className="pr-4">محصول</TableHead>
                  <TableHead>توضیحات</TableHead>
                  <TableHead>مبلغ</TableHead>
                  <TableHead>مدت</TableHead>
                  <TableHead>سفارش‌ها</TableHead>
                  <TableHead>فعال</TableHead>
                  <TableHead className="text-center">عملیات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="pr-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">
                          {p.icon || "🤖"}
                        </span>
                        <span className="font-medium">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[18rem] truncate text-xs text-muted-foreground">
                      {p.description || "—"}
                    </TableCell>
                    <TableCell className="text-sm font-semibold text-primary">
                      {faToman(p.priceToman)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {p.duration || (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {faNum(p._count?.orders ?? 0)}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={p.active}
                        onCheckedChange={(v) => toggleActive(p, v)}
                        disabled={togglingId === p.id}
                        aria-label="فعال/غیرفعال"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="ویرایش"
                          onClick={() => openEdit(p)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="حذف"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleteTarget(p)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Create / Edit form dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => !o && setFormOpen(false)}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing ? "ویرایش محصول" : "افزودن محصول جدید"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "اطلاعات محصول را ویرایش و ذخیره کنید."
                : "یک محصول هوش مصنوعی جدید تعریف کنید."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForm} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_5rem]">
              <div className="flex flex-col gap-2">
                <Label htmlFor="prod-name">نام محصول *</Label>
                <Input
                  id="prod-name"
                  placeholder="مثلاً: اکانت ChatGPT Plus"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  disabled={submitting}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="prod-icon">آیکون</Label>
                <Input
                  id="prod-icon"
                  placeholder="🤖"
                  value={form.icon}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, icon: e.target.value }))
                  }
                  disabled={submitting}
                  className="text-center text-lg"
                  maxLength={4}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="prod-desc">توضیحات</Label>
              <Textarea
                id="prod-desc"
                placeholder="توضیح کوتاه درباره محصول..."
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                disabled={submitting}
                className="min-h-16 text-sm"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="prod-price">مبلغ (تومان) *</Label>
                <Input
                  id="prod-price"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="100000"
                  value={form.priceToman}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      priceToman: e.target.value.replace(/[^\d]/g, ""),
                    }))
                  }
                  disabled={submitting}
                  className="text-left"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="prod-duration">مدت اعتبار</Label>
                <Input
                  id="prod-duration"
                  placeholder="مثلاً: ۳۰ روز"
                  value={form.duration}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, duration: e.target.value }))
                  }
                  disabled={submitting}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="prod-sort">ترتیب نمایش</Label>
                <Input
                  id="prod-sort"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="0"
                  value={form.sortOrder}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      sortOrder: e.target.value.replace(/[^\d]/g, ""),
                    }))
                  }
                  disabled={submitting}
                  className="text-left"
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border bg-muted/40 p-3">
              <div>
                <Label htmlFor="prod-active" className="text-sm">
                  فعال
                </Label>
                <p className="text-xs text-muted-foreground">
                  محصولات غیرفعال در فروشگاه دیده نمی‌شوند.
                </p>
              </div>
              <Switch
                id="prod-active"
                checked={form.active}
                onCheckedChange={(v) =>
                  setForm((f) => ({ ...f, active: v }))
                }
                disabled={submitting}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormOpen(false)}
                disabled={submitting}
              >
                انصراف
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    در حال ذخیره...
                  </>
                ) : editing ? (
                  "ذخیره تغییرات"
                ) : (
                  "ایجاد محصول"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف محصول</AlertDialogTitle>
            <AlertDialogDescription>
              آیا از حذف محصول «{deleteTarget?.name}» مطمئن هستید؟ این عمل قابل
              بازگشت نیست.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>انصراف</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {deleting ? "در حال حذف..." : "حذف"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
